import React, { useState, useEffect, useRef } from 'react';
import logger from '../../utils/logger';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { TextArea } from '../ui/TextArea';
import Checkbox from '../ui/Checkbox';
import {
  BusinessPartner,
  BusinessPartnerType,
  BusinessPartnerStatus,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  BusinessPartnerRole,
  DeadlineRule,
} from '../../types';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { DeadlineRuleInlineEditor } from './DeadlineRuleInlineEditor';
import { DeadlineRuleInlineEditorRef } from '@/types/deadlineRule';
import { SupabaseDeadlineRuleService } from '../../services/supabase/supabaseDeadlineRuleService';
import { useAuth } from '../../hooks/useAuth';
import { useMasterData } from '../../hooks/useMasterData';

interface BusinessPartnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<BusinessPartner>) => Promise<void>;
  partner?: BusinessPartner | null;
  mode: 'create' | 'edit';
}

interface FormData {
  name: string;
  displayName: string;
  type: BusinessPartnerType;
  status: BusinessPartnerStatus;
  dataSource: BusinessPartnerDataSource;
  category: BusinessPartnerCategory;
  availableRoles: BusinessPartnerRole[];
  defaultAddress: string;
  defaultPhone: string;
  defaultContactPerson: string;
  defaultEmail: string;
  notes: string;
}

const initialFormData: FormData = {
  name: '',
  displayName: '',
  type: BusinessPartnerType.CORPORATE,
  status: BusinessPartnerStatus.ACTIVE,
  dataSource: BusinessPartnerDataSource.RAKUHAI_PROVIDED,
  category: BusinessPartnerCategory.DEALERSHIP,
  availableRoles: [
    BusinessPartnerRole.BILLING, // 請求先
    BusinessPartnerRole.PICKUP, // 引き取り先
    BusinessPartnerRole.DELIVERY, // 搬入先
  ],
  defaultAddress: '',
  defaultPhone: '',
  defaultContactPerson: '',
  defaultEmail: '',
  notes: '',
};

export const BusinessPartnerFormModal: React.FC<BusinessPartnerFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  partner,
  mode,
}) => {
  const { user } = useAuth();
  const { getCompanyDeadlineRuleSettingsByCompanyId } = useMasterData();
  const [formData, setFormData] = useState<FormData>(initialFormData);

  // 締切ルール機能が有効かどうかをチェック
  const deadlineRuleSettings = user?.companyId
    ? getCompanyDeadlineRuleSettingsByCompanyId(user.companyId)
    : null;
  const isDeadlineRulesEnabled = deadlineRuleSettings?.deadlineRulesEnabled ?? true; // デフォルトで有効（設定が保存されていない場合）

  // 締切ルール設定確認
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 締切ルール関連のstate
  const [deadlineRules, setDeadlineRules] = useState<DeadlineRule[]>([]);
  const [isDeadlineRulesExpanded, setIsDeadlineRulesExpanded] = useState(false); // デフォルトは閉じた状態、締切ルールの有無に応じて自動調整
  const deadlineRuleService = new SupabaseDeadlineRuleService();

  // DeadlineRuleInlineEditorへの参照
  const deadlineRuleEditorRef = useRef<DeadlineRuleInlineEditorRef>(null);

  // パートナーデータをフォームに設定
  useEffect(() => {
    if (partner && mode === 'edit') {
      setFormData({
        name: partner.name || '',
        displayName: partner.displayName || '',
        type: partner.type || BusinessPartnerType.CORPORATE,
        status: partner.status || BusinessPartnerStatus.ACTIVE,
        dataSource: partner.dataSource || BusinessPartnerDataSource.RAKUHAI_PROVIDED,
        category: partner.category || BusinessPartnerCategory.DEALERSHIP,
        availableRoles: partner.availableRoles || [BusinessPartnerRole.PICKUP],
        defaultAddress: partner.defaultAddress || '',
        defaultPhone: partner.defaultPhone || '',
        defaultContactPerson: partner.defaultContactPerson || '',
        defaultEmail: partner.defaultEmail || '',
        notes: partner.notes || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [partner, mode, isOpen]);

  // 締切ルールの読み込み
  useEffect(() => {
    const loadDeadlineRules = async () => {
      if (!partner?.id || !user?.companyId) {
        setDeadlineRules([]);
        setIsDeadlineRulesExpanded(false);
        return;
      }

      try {
        const rules = await deadlineRuleService.getDeadlineRulesByBusinessPartnerId(
          partner.id,
          user.companyId
        );
        setDeadlineRules(rules);

        // 非同期状態更新を次のティックに遅延させてReactの再レンダリングサイクルを確実にする
        const shouldOpen = rules.length > 0;
        if (shouldOpen) {
          setTimeout(() => setIsDeadlineRulesExpanded(true), 0);
        } else {
          setIsDeadlineRulesExpanded(false);
        }
      } catch (error) {
        console.error('Failed to load deadline rules:', error);
        setDeadlineRules([]);
        setIsDeadlineRulesExpanded(false);
      }
    };

    loadDeadlineRules();
  }, [partner?.id, user?.companyId, isOpen, mode]);

  // フォーム検証
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '取引先名は必須です';
    }

    if (!formData.defaultAddress.trim()) {
      newErrors.defaultAddress = '住所は必須です';
    }

    if (!formData.defaultPhone.trim()) {
      newErrors.defaultPhone = '電話番号は必須です';
    }

    if (formData.defaultEmail && !formData.defaultEmail.includes('@')) {
      newErrors.defaultEmail = '有効なメールアドレスを入力してください';
    }

    if (formData.availableRoles.length === 0) {
      newErrors.availableRoles = '請求先、引き取り先、搬入先のいずれか1つ以上を選択してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フィールド更新
  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // 役割チェックボックス更新
  const handleRoleChange = (role: BusinessPartnerRole, checked: boolean) => {
    logger.info('🔄 役割チェックボックス変更:', {
      role,
      checked,
      currentRoles: formData.availableRoles,
    });

    const currentRoles = formData.availableRoles;
    const newRoles = checked ? [...currentRoles, role] : currentRoles.filter(r => r !== role);

    logger.info('📝 新しい役割配列:', newRoles);

    // 役割の更新（エラーはクリアしない）
    setFormData(prev => ({
      ...prev,
      availableRoles: newRoles,
    }));

    // 役割が選択された場合のみエラーをクリア
    if (newRoles.length > 0 && errors.availableRoles) {
      setErrors(prev => ({
        ...prev,
        availableRoles: '',
      }));
    }

    logger.info('✅ 役割更新完了');
  };

  // 締切ルールの適用（新しいローカルステート管理方式）
  const applyDeadlineRuleChanges = async (): Promise<void> => {
    logger.info('締切ルール適用開始:', {
      mode,
      partnerId: partner?.id,
      enabled: isDeadlineRulesEnabled,
    });

    if (!isDeadlineRulesEnabled) {
      return;
    }

    if (!deadlineRuleEditorRef.current) {
      logger.warn('締切ルールエディタリファレンスが見つかりません');
      return;
    }

    await executeDeadlineRuleChanges();
  };

  // 締切ルール変更の実行ロジック
  const executeDeadlineRuleChanges = async () => {
    try {
      const { toCreate, toUpdate, toDelete } = deadlineRuleEditorRef.current!.getModifiedRules();

      logger.info('🚀 executeDeadlineRuleChanges開始', {
        partnerId: partner?.id,
        partnerName: partner?.name,
        toCreateCount: toCreate.length,
        toUpdateCount: toUpdate.length,
        toDeleteCount: toDelete.length,
        toCreate,
        toUpdate,
        toDelete: toDelete.map((d: DeadlineRule) => ({
          id: d.id,
          ruleType: d.ruleType,
          dayOfWeek: d.dayOfWeek,
          isActive: d.isActive,
        })),
      });

      if (toCreate.length > 0 || toUpdate.length > 0 || toDelete.length > 0) {
        logger.info('🔄 締切ルール変更検出:', {
          toCreate: toCreate.length,
          toUpdate: toUpdate.length,
          toDelete: toDelete.length,
        });
      } else {
        logger.info('ℹ️ 変更なし、処理をスキップ');
        return;
      }

      // 削除処理
      for (const [index, rule] of toDelete.entries()) {
        logger.info(`🗑️ [${index + 1}/${toDelete.length}] 削除処理開始`, {
          ruleId: rule.id,
          ruleType: rule.ruleType,
          dayOfWeek: rule.dayOfWeek,
          isActive: rule.isActive,
        });

        try {
          await deadlineRuleService.deleteDeadlineRule(rule.id);
          logger.info(`✅ [${index + 1}/${toDelete.length}] 削除成功`, {
            ruleId: rule.id,
          });
        } catch (error) {
          logger.error(`❌ [${index + 1}/${toDelete.length}] 削除失敗`, {
            ruleId: rule.id,
            error,
          });
          throw error;
        }
      }

      // 作成処理
      for (const [index, input] of toCreate.entries()) {
        logger.info(`📝 [${index + 1}/${toCreate.length}] 作成処理開始`, {
          ruleType: input.ruleType,
          dayOfWeek: input.dayOfWeek,
          deadlineTime: input.deadlineTime,
          isActive: input.isActive,
          businessPartnerId: input.businessPartnerId,
          companyId: input.companyId,
        });

        try {
          const createdRule = await deadlineRuleService.createDeadlineRule({
            ...input,
            companyId: user?.companyId || '',
          });
          logger.info(`✅ [${index + 1}/${toCreate.length}] 作成成功`, {
            createdRuleId: createdRule.id,
            input: input,
          });
        } catch (error) {
          logger.error(`❌ [${index + 1}/${toCreate.length}] 作成失敗`, {
            input,
            error,
          });
          throw error;
        }
      }

      // 更新処理
      for (const [index, { id, input }] of toUpdate.entries()) {
        logger.info(`🔄 [${index + 1}/${toUpdate.length}] 更新処理開始`, {
          ruleId: id,
          input,
        });

        try {
          await deadlineRuleService.updateDeadlineRule(id, input);
          logger.info(`✅ [${index + 1}/${toUpdate.length}] 更新成功`, {
            ruleId: id,
            input,
          });
        } catch (error) {
          logger.error(`❌ [${index + 1}/${toUpdate.length}] 更新失敗`, {
            ruleId: id,
            input,
            error,
          });
          throw error;
        }
      }

      // 成功した場合、最新のルールを再読み込み
      if (partner?.id) {
        logger.info('🔄 最新ルール再読み込み開始', {
          partnerId: partner.id,
        });

        try {
          const updatedRules = await deadlineRuleService.getDeadlineRulesByBusinessPartnerId(
            partner.id
          );
          logger.info('✅ 最新ルール再読み込み成功', {
            updatedRulesCount: updatedRules.length,
            updatedRules: updatedRules.map(r => ({
              id: r.id,
              ruleType: r.ruleType,
              dayOfWeek: r.dayOfWeek,
              isActive: r.isActive,
              deadlineTime: r.deadlineTime,
            })),
          });

          setDeadlineRules(updatedRules);
          // ルールが全て削除された場合は自動でアコーディオンを閉じる
          setIsDeadlineRulesExpanded(updatedRules.length > 0);

          logger.info('🎯 executeDeadlineRuleChanges完了', {
            finalRulesCount: updatedRules.length,
          });
        } catch (error) {
          logger.error('❌ 最新ルール再読み込み失敗', {
            error,
          });
          throw error;
        }
      }
    } catch (error) {
      logger.error('💥 executeDeadlineRuleChanges失敗', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      });
      throw error; // エラーを上位に伝播
    }
  };

  // フォーム送信
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);

      // 1. 編集モードで締切ルール機能が有効な場合、先に締切ルールを適用（refがまだ有効）
      if (mode === 'edit' && isDeadlineRulesEnabled) {
        await applyDeadlineRuleChanges();
      }

      // 2. 締切ルール更新が成功した後、取引先情報を保存
      const saveData: Partial<BusinessPartner> = {
        ...formData,
        locations: [
          {
            id: `${Date.now()}`,
            name: 'メイン拠点',
            address: formData.defaultAddress,
            phone: formData.defaultPhone,
            contactPerson: formData.defaultContactPerson,
            email: formData.defaultEmail,
            isDefault: true,
            roles: formData.availableRoles,
            businessHours: {
              weekday: { open: '09:00', close: '18:00' },
              saturday: { open: '09:00', close: '17:00' },
              sunday: { open: '', close: '' },
            },
          },
        ],
      };

      if (mode === 'edit' && partner) {
        saveData.id = partner.id;
      }

      await onSave(saveData);

      onClose();
    } catch (error) {
      logger.error('保存処理エラー:', error);
      // エラーの種類に応じてメッセージを分岐
      if (error instanceof Error && error.message.includes('締切ルール')) {
        setErrors({ submit: '締切ルールの保存に失敗しました' });
      } else {
        setErrors({ submit: '取引先の保存に失敗しました' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // キャンセル処理
  const handleCancel = () => {
    // 締切ルールの変更をキャンセル
    if (deadlineRuleEditorRef.current && mode === 'edit' && isDeadlineRulesEnabled) {
      deadlineRuleEditorRef.current.cancelChanges();
    }
    onClose();
  };

  // ラベル生成関数
  const getTypeLabel = (type: BusinessPartnerType): string => {
    const labels = {
      [BusinessPartnerType.CORPORATE]: '法人',
      [BusinessPartnerType.INDIVIDUAL]: '個人',
      [BusinessPartnerType.DEALER]: 'ディーラー',
      [BusinessPartnerType.AUCTION]: 'オークション会場',
      [BusinessPartnerType.WORKSHOP]: '整備工場',
      [BusinessPartnerType.INSURANCE]: '保険会社',
      [BusinessPartnerType.OTHER]: 'その他',
    };
    return labels[type] || type;
  };

  const getCategoryLabel = (category: BusinessPartnerCategory): string => {
    const labels = {
      [BusinessPartnerCategory.DEALERSHIP]: '売買・買取店',
      [BusinessPartnerCategory.DEALER]: '売買店',
      [BusinessPartnerCategory.AUCTION]: 'オークション会場',
      [BusinessPartnerCategory.OTHER]: 'その他',
    };
    return labels[category] || category;
  };

  const getRoleLabel = (role: BusinessPartnerRole): string => {
    const labels = {
      [BusinessPartnerRole.BILLING]: '請求先',
      [BusinessPartnerRole.PICKUP]: '引き取り先',
      [BusinessPartnerRole.PICKUP_LOCATION]: '引取拠点',
      [BusinessPartnerRole.DELIVERY]: '搬入先',
      [BusinessPartnerRole.DELIVERY_LOCATION]: '搬入拠点',
      [BusinessPartnerRole.BOTH]: '引取・搬入両方',
    };
    return labels[role] || role;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={mode === 'create' ? '新規取引先追加' : '取引先編集'}
      size='xl'
    >
      <form onSubmit={handleSubmit} className='space-y-6' aria-labelledby='modal-title'>
        {errors.submit && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 mr-2' />
              <p className='text-sm text-red-800 dark:text-red-200'>{errors.submit}</p>
            </div>
          </div>
        )}

        {/* 基本情報 */}
        <div className='grid grid-cols-1 gap-6'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2'>
            基本情報
          </h3>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <Input
                label='取引先名 *'
                value={formData.name}
                onChange={e => updateField('name', e.target.value)}
                error={errors.name}
                placeholder='例: 株式会社○○モータース'
              />
            </div>
            <div>
              <Input
                label='表示名'
                value={formData.displayName}
                onChange={e => updateField('displayName', e.target.value)}
                error={errors.displayName}
                placeholder='例: ○○モータース'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div>
              <Select
                label='事業者種別'
                value={formData.type}
                onChange={e => updateField('type', e.target.value as BusinessPartnerType)}
                error={errors.type}
                options={Object.values(BusinessPartnerType).map(type => ({
                  value: type,
                  label: getTypeLabel(type),
                }))}
              />
            </div>
            <div>
              <Select
                label='カテゴリー'
                value={formData.category}
                onChange={e => updateField('category', e.target.value as BusinessPartnerCategory)}
                error={errors.category}
                options={Object.values(BusinessPartnerCategory).map(category => ({
                  value: category,
                  label: getCategoryLabel(category),
                }))}
              />
            </div>
            <div>
              <Select
                label='ステータス'
                value={formData.status}
                onChange={e => updateField('status', e.target.value as BusinessPartnerStatus)}
                error={errors.status}
                options={[
                  { value: BusinessPartnerStatus.ACTIVE, label: '有効' },
                  { value: BusinessPartnerStatus.INACTIVE, label: '無効' },
                  { value: BusinessPartnerStatus.SUSPENDED, label: '停止中' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* 連絡先情報 */}
        <div className='grid grid-cols-1 gap-6'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2'>
            連絡先情報
          </h3>

          <div className='grid grid-cols-1 gap-4'>
            <div>
              <Input
                label='住所 *'
                value={formData.defaultAddress}
                onChange={e => updateField('defaultAddress', e.target.value)}
                error={errors.defaultAddress}
                placeholder='例: 東京都千代田区...'
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Input
                  label='電話番号 *'
                  value={formData.defaultPhone}
                  onChange={e => updateField('defaultPhone', e.target.value)}
                  error={errors.defaultPhone}
                  placeholder='例: 03-1234-5678'
                />
              </div>
              <div>
                <Input
                  label='メールアドレス'
                  value={formData.defaultEmail}
                  onChange={e => updateField('defaultEmail', e.target.value)}
                  error={errors.defaultEmail}
                  placeholder='例: contact@example.com'
                />
              </div>
            </div>

            <div>
              <Input
                label='担当者名'
                value={formData.defaultContactPerson}
                onChange={e => updateField('defaultContactPerson', e.target.value)}
                error={errors.defaultContactPerson}
                placeholder='例: 田中太郎'
              />
            </div>
          </div>
        </div>

        {/* 利用可能な役割 */}
        <div className='grid grid-cols-1 gap-6'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2'>
            利用可能な役割 *
          </h3>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            {[
              BusinessPartnerRole.BILLING,
              BusinessPartnerRole.PICKUP,
              BusinessPartnerRole.DELIVERY,
            ].map(role => (
              <div key={role}>
                <Checkbox
                  label={getRoleLabel(role)}
                  checked={formData.availableRoles.includes(role)}
                  onChange={(checked: boolean) => handleRoleChange(role, checked)}
                />
              </div>
            ))}
          </div>

          {errors.availableRoles && (
            <p className='text-sm text-red-600 dark:text-red-400'>{errors.availableRoles}</p>
          )}
        </div>

        {/* 締切ルール設定（編集モードのみ） */}
        {mode === 'edit' && partner?.id && isDeadlineRulesEnabled && (
          <div className='border-t border-gray-200 dark:border-gray-700 pt-6'>
            {/* アコーディオンヘッダー */}
            <button
              type='button'
              onClick={() => setIsDeadlineRulesExpanded(!isDeadlineRulesExpanded)}
              className='w-full flex items-center justify-between mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-t-lg px-2 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              aria-expanded={isDeadlineRulesExpanded}
            >
              <div className='flex items-center'>
                <CalendarDaysIcon className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2' />
                <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                  締切ルール設定 ({deadlineRules.length}件)
                </h3>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='text-sm text-gray-500 dark:text-gray-400'>
                  時間制限や曜日別の締切時間を設定
                </span>
                {isDeadlineRulesExpanded ? (
                  <ChevronUpIcon className='h-5 w-5 text-gray-500 dark:text-gray-400' />
                ) : (
                  <ChevronDownIcon className='h-5 w-5 text-gray-500 dark:text-gray-400' />
                )}
              </div>
            </button>

            {/* アコーディオンコンテンツ */}
            {isDeadlineRulesExpanded && (
              <div className='transition-all duration-200 ease-in-out'>
                <DeadlineRuleInlineEditor
                  key={`${partner.id}-${deadlineRules.length}-${deadlineRules.map(r => r.id).join(',')}`}
                  ref={deadlineRuleEditorRef}
                  rules={deadlineRules}
                  businessPartnerId={partner.id}
                  businessPartnerName={partner.name}
                  companyId={user?.companyId || ''}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        )}

        {/* 備考 */}
        <div>
          <TextArea
            label='備考・特記事項'
            value={formData.notes}
            onChange={e => updateField('notes', e.target.value)}
            error={errors.notes}
            placeholder='搬入条件、注意事項等があれば記入してください'
            rows={3}
          />
        </div>

        {/* アクションボタン */}
        <div className='flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700'>
          <Button type='button' variant='ghost' onClick={handleCancel} disabled={isLoading}>
            キャンセル
          </Button>
          <Button type='submit' disabled={isLoading} loading={isLoading}>
            {mode === 'create' ? '追加' : '更新'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
